import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { editCompany } from '../../apis/CompanyAPI';
import { MenuItem, Select } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

export default function EditCompany({ data, onCompanyEditted }) {
    const [open, setOpen] = React.useState(false);
    const [selectedTid, setSelectedTid] = React.useState('');
    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [credits, setCredits] = React.useState(0);
    const [recurringCredits, setRecurringCredits] = React.useState(0);
    const [isActive, setIsActive] = React.useState(true);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        // Reset fields
        setSelectedTid('');
        setName('');
        setDescription('');
        setCredits(0);
        setRecurringCredits(0);
        setIsActive(true);
    };

    const handleSubmit = async () => {
        try {
            await editCompany({
                name: name,
                description: description,
                meeting_creds: Number(credits),
                recurring_credits: Number(recurringCredits),
                tid: selectedTid,
                reg_code: "False",
                is_active: isActive ? 1 : 0
            });
            handleClose();
            onCompanyEditted();
        }
        catch (error) {
            console.error('Error editting Company:', error);
        }
    };

    const [confirmOpen, setConfirmOpen] = React.useState(false);

    const handleStatusChange = (newStatus) => {
        if (newStatus === false && isActive === true) {
            setConfirmOpen(true);
        } else {
            setIsActive(newStatus);
        }
    };

    const confirmDeactivation = () => {
        setIsActive(false);
        setConfirmOpen(false);
    };

    const cancelDeactivation = () => {
        setConfirmOpen(false);
        // Reset the Select value conceptually (though it hasn't changed in state yet)
    };

    return (
        <React.Fragment>
            <EditNoteOutlinedIcon onClick={handleClickOpen} sx={{
                color: "black",
                background: "none",
                border: "none",
                float: "right",
                margin: "0px 0px 0px 10px",
                cursor: "pointer"
            }} />
            <Dialog
                open={open}
                onClose={handleClose}
                PaperProps={{
                    component: 'form',
                    onSubmit: (event) => {
                        event.preventDefault();
                        handleSubmit();
                    },
                }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Edit Company</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{paddingBottom: 3}}>
                        Select a company to edit, then modify the fields below.
                    </DialogContentText>
                   {/* ... fields ... */}
                     <FormControl fullWidth>
                        <InputLabel id="select-company-label">Select Company</InputLabel>
                        <Select
                            required
                            labelId='select-company-label'
                            id='select-company'
                            value={selectedTid}
                            label='Select Company'
                            fullWidth
                            onChange={(e) => {
                                const tid = e.target.value;
                                const selectedCompany = data.find(company => company.tid === tid);
                                if (selectedCompany) {
                                    setSelectedTid(tid);
                                    setName(selectedCompany.name);
                                    setDescription(selectedCompany.description);
                                    setCredits(selectedCompany.meeting_cred);
                                    setRecurringCredits(selectedCompany.recurring_credits || 0);
                                    setIsActive(selectedCompany.is_active !== 0 && selectedCompany.is_active !== false);
                                }
                            }}
                        >
                            {data.map(company => (
                                <MenuItem key={company.tid} value={company.tid}>
                                    {company.name} {company.is_active === 0 ? "(Inactive)" : ""}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        required
                        sx={{ mt: 3 }}
                        name='name'
                        value={name}
                        margin='dense'
                        id='name'
                        label='Company Name'
                        type='text'
                        fullWidth
                        variant='outlined'
                        disabled={!selectedTid || !isActive}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <TextField
                        required
                        sx={{ mt: 3 }}
                        name='description'
                        value={description}
                        margin='dense'
                        id='description'
                        label='Description'
                        type='text'
                        fullWidth
                        variant='outlined'
                        disabled={!selectedTid || !isActive}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                    <TextField
                        required
                        sx={{ mt: 3 }}
                        name='credits'
                        value={credits}
                        margin="dense"
                        id="credits"
                        label="Credits"
                        type="number"
                        fullWidth
                        variant="outlined"
                        disabled={!selectedTid || !isActive}
                        onChange={(e) => setCredits(e.target.value)}
                    />
                    <TextField
                        required
                        sx={{ mt: 3 }}
                        name='recurringCredits'
                        value={recurringCredits}
                        margin="dense"
                        id="recurringCredits"
                        label="Recurring Credits"
                        type="number"
                        fullWidth
                        variant="outlined"
                        disabled={!selectedTid || !isActive}
                        onChange={(e) => setRecurringCredits(e.target.value)}
                    />
                    
                    <FormControl fullWidth sx={{ mt: 3 }}>
                        <InputLabel id="status-label">Status</InputLabel>
                        <Select
                            labelId="status-label"
                            id="status-select"
                            value={isActive ? 1 : 0}
                            label="Status"
                            onChange={(e) => handleStatusChange(e.target.value === 1)}
                            disabled={!selectedTid}
                        >
                            <MenuItem value={1}>Active</MenuItem>
                            <MenuItem value={0} sx={{ color: 'red' }}>Inactive</MenuItem>
                        </Select>
                    </FormControl>

                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", paddingBottom: "20px" }}>
                    <Button onClick={handleClose} variant='outlined'>Cancel</Button>
                    <Button type="submit" variant='contained' disabled={!selectedTid}>Submit</Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmOpen}
                onClose={cancelDeactivation}
            >
                <DialogTitle>{"Deactivate Company?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Warning: Deactivating this company will also deactivate ALL users associated with it. This action cannot be undone easily.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDeactivation} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={confirmDeactivation} color="error" autoFocus>
                        Deactivate
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
